import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useWeb3 } from "../context/Web3Context"; // ✅ Contexte global Web3

// ✅ Variables d’environnement
const ADMIN_WALLET = (process.env.REACT_APP_ADMIN_WALLET || "").toLowerCase();

export default function Layout({ children }) {
  const vantaRef = useRef(null);
  const vantaEffect = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  // === Web3 (MetaMask) ===
  const { account, connectWallet, disconnectWallet, isConnected } = useWeb3();
  const shortAddr = account ? `${account.slice(0, 6)}...${account.slice(-4)}` : "";

  // === Fond VANTA Globe ===
  useEffect(() => {
    let cleanup = false;
    const initVanta = async () => {
      try {
        const { default: THREE } = await import("three");
        const { default: VANTA } = await import("vanta/dist/vanta.globe.min.js");

        if (cleanup || !vantaRef.current) return;

        vantaEffect.current = VANTA({
          el: vantaRef.current,
          THREE,
          mouseControls: true,
          touchControls: true,
          gyroControls: false,
          minHeight: 200.0,
          minWidth: 200.0,
          scale: 1.0,
          scaleMobile: 1.0,
          color: 0x9d4edd,
          backgroundColor: 0x0f172a,
        });

        setTimeout(() => setIsLoading(false), 800);
      } catch (err) {
        console.error("Erreur chargement VANTA:", err);
        setIsLoading(false);
      }
    };

    initVanta();

    return () => {
      cleanup = true;
      if (vantaEffect.current) {
        vantaEffect.current.destroy();
        vantaEffect.current = null;
      }
    };
  }, []);

  return (
    <div className="bg-darkBg text-white min-h-screen relative overflow-hidden">
      {/* === Loader initial === */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-darkBg">
          <div className="w-10 h-10 border-4 border-neonPurple border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-neonPink text-lg font-semibold animate-pulse">
            Initializing NEXORA universe...
          </p>
        </div>
      )}

      {/* === Fond 3D === */}
      <div ref={vantaRef} className="fixed inset-0 -z-10 opacity-40" />

      {/* === NAVBAR === */}
      <nav className="container mx-auto px-6 py-4 flex justify-between items-center relative z-20 backdrop-blur-md bg-gray-900/30 border-b border-gray-800/50 shadow-lg">
        {/* === Logo === */}
        <Link to="/" className="flex items-center gap-2 group">
          <img
            src="/images/logo.svg"
            alt="NEXORA Logo"
            className="w-10 h-10 rounded-full border border-neonPurple shadow-md transition-all duration-500 transform group-hover:scale-110 group-hover:rotate-6"
          />
          <span className="text-2xl font-bold bg-gradient-to-r from-neonPink to-neonBlue bg-clip-text text-transparent group-hover:from-neonBlue group-hover:to-neonPink transition-all">
            NEXORA
          </span>
        </Link>

        {/* === Menu Desktop === */}
        <div className="hidden md:flex space-x-8 items-center">
          {["Home", "About", "Projects", "Tokenomics", "Roadmap", "Buy", "FAQ", "Contact", "Dashboard"].map(
            (page) => (
              <Link
                key={page}
                to={page === "Home" ? "/" : `/${page.toLowerCase()}`}
                className="hover:text-neonPink transition-all"
              >
                {page}
              </Link>
            )
          )}

          {/* 👑 Lien Admin visible uniquement si wallet admin */}
          {account && account.toLowerCase() === ADMIN_WALLET && (
            <Link to="/admin" className="text-neonPink font-bold ml-4">
              Admin
            </Link>
          )}

          {/* 🔐 Bouton Connect / Disconnect */}
          <button
            onClick={() => {
              if (!window.ethereum) {
                alert("🦊 Please install MetaMask!");
                return;
              }
              isConnected ? disconnectWallet() : connectWallet();
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              isConnected
                ? "bg-gray-700 hover:bg-gray-600"
                : "bg-gradient-to-r from-neonPurple to-neonPink hover:opacity-90"
            }`}
          >
            {isConnected ? shortAddr : "Connect Wallet"}
          </button>
        </div>

        {/* === Menu Mobile === */}
        <button
          className="md:hidden flex flex-col justify-center items-center space-y-1"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span
            className={`block w-6 h-0.5 bg-white transition-all ${
              menuOpen ? "rotate-45 translate-y-2" : ""
            }`}
          ></span>
          <span
            className={`block w-6 h-0.5 bg-white transition-all ${
              menuOpen ? "opacity-0" : ""
            }`}
          ></span>
          <span
            className={`block w-6 h-0.5 bg-white transition-all ${
              menuOpen ? "-rotate-45 -translate-y-2" : ""
            }`}
          ></span>
        </button>

        {menuOpen && (
          <div className="absolute top-full left-0 w-full bg-gray-900/95 backdrop-blur-md rounded-b-xl flex flex-col items-center space-y-4 py-6 md:hidden animate-fadeIn border-t border-gray-700 shadow-lg">
            {["Home", "About", "Projects", "Tokenomics", "Roadmap", "Buy", "FAQ", "Contact", "Dashboard"].map(
              (page) => (
                <Link
                  key={page}
                  to={page === "Home" ? "/" : `/${page.toLowerCase()}`}
                  className="text-lg hover:text-neonPink transition-all"
                  onClick={() => setMenuOpen(false)}
                >
                  {page}
                </Link>
              )
            )}

            {account && account.toLowerCase() === ADMIN_WALLET && (
              <Link
                to="/admin"
                className="text-neonPink font-semibold"
                onClick={() => setMenuOpen(false)}
              >
                Admin
              </Link>
            )}

            <button
              onClick={() => {
                if (!window.ethereum) {
                  alert("🦊 Please install MetaMask!");
                  return;
                }
                isConnected ? disconnectWallet() : connectWallet();
                setMenuOpen(false);
              }}
              className={`px-6 py-2 rounded-lg font-semibold ${
                isConnected
                  ? "bg-gray-700 hover:bg-gray-600"
                  : "bg-gradient-to-r from-neonPurple to-neonPink hover:opacity-90"
              }`}
            >
              {isConnected ? shortAddr : "Connect Wallet"}
            </button>
          </div>
        )}
      </nav>

      {/* === CONTENU === */}
      <main className="container mx-auto px-4 py-16 max-w-6xl">{children}</main>

      {/* === FOOTER === */}
      <footer className="bg-gray-900 bg-opacity-70 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">About NEXORA</h3>
              <p className="text-gray-400">
                Empowering the next generation through blockchain technology.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {[
                  ["Tokenomics", "/tokenomics"],
                  ["Roadmap", "/roadmap"],
                  ["Whitepaper", "/Whitepaper"],
                  ["Projects", "/projects"],
                  ["Buy NEXORA", "/buy"],
                  ["FAQ", "/faq"],
                  
                ].map(([label, to]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-gray-400 hover:text-neonPink transition-all"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Community</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="https://discord.gg/9TJmUf4t"
                    className="text-gray-400 hover:text-neonBlue"
                  >
                    Discord
                  </a>
                </li>
                <li>
                  <a
                    href="https://x.com/nexoralocker"
                    className="text-gray-400 hover:text-neonBlue"
                  >
                    Twitter
                  </a>
                </li>
                <li>
                  <a
                    href="https://t.me/NXOCommunity"
                    className="text-gray-400 hover:text-neonBlue"
                  >
                    Telegram
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            © 2025 NEXORA Token. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
